import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import {
  SignalCellularAlt as SignalIcon,
  Wifi as WifiIcon,
  BatteryFull as BatteryIcon,
} from '@mui/icons-material';

/**
 * Phone-frame visual wrapper — a fixed 360x780 viewport styled as an Android phone.
 * The "device" fills the inner area; children render inside the screen.
 *
 * These are web mockups showing what the Android app will LOOK like. Not a real Android app.
 */
export const PHONE_WIDTH = 360;
export const PHONE_HEIGHT = 780;

export const StatusBar = ({ dark = false }) => {
  const fg = dark ? '#fff' : '#1b1b1f';
  return (
    <Box
      sx={{
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        color: fg,
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ color: fg, fontWeight: 700, fontSize: '0.72rem' }}>
        9:41
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <SignalIcon sx={{ fontSize: 13, color: fg }} />
        <WifiIcon sx={{ fontSize: 13, color: fg }} />
        <BatteryIcon sx={{ fontSize: 14, color: fg }} />
      </Stack>
    </Box>
  );
};

export const AutomatrixBanner = () => (
  <Box
    sx={{
      bgcolor: '#0b847f',
      color: '#fff',
      px: 2,
      py: 0.5,
      fontSize: '0.68rem',
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      flexShrink: 0,
    }}
  >
    <Box sx={{
      width: 14, height: 14, borderRadius: '3px',
      background: 'linear-gradient(135deg, #1ebea5 0%, #0b847f 100%)',
      border: '1px solid rgba(255,255,255,0.6)',
      display: 'grid', placeItems: 'center',
      fontFamily: 'system-ui', fontWeight: 800, fontSize: '8px',
      color: '#fff',
    }}>A</Box>
    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.68rem' }}>
      AchieveDXP Android v1.0
    </Typography>
  </Box>
);

export const NavBar = ({ label = '', dark = false }) => {
  const fg = dark ? '#fff' : '#1b1b1f';
  return (
    <Box
      sx={{
        height: 42,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        bgcolor: dark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.02)',
        flexShrink: 0,
      }}
    >
      {/* Android gesture bar */}
      <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${fg}`, opacity: 0.6 }} />
      <Box sx={{ width: 100, height: 4, borderRadius: 2, bgcolor: fg, opacity: 0.7 }} />
      <Box sx={{ width: 12, height: 12, border: `1.5px solid ${fg}`, opacity: 0.6 }} />
      {label ? (
        <Typography variant="caption" sx={{ position: 'absolute', bottom: 2, fontSize: '0.55rem', color: fg, opacity: 0.5 }}>
          {label}
        </Typography>
      ) : null}
    </Box>
  );
};

const PhoneFrame = ({ children, statusDark = false, background = '#f6f7fb' }) => (
  <Box
    sx={{
      width: PHONE_WIDTH + 20,
      maxWidth: PHONE_WIDTH + 20,
      mx: 'auto',
      my: 2,
    }}
  >
    <Box
      sx={{
        width: PHONE_WIDTH + 20,
        height: PHONE_HEIGHT + 20,
        borderRadius: 8, // 32px via MUI theme-spacing (8 * 4)
        bgcolor: '#111216',
        p: '10px',
        boxShadow: '0 30px 60px rgba(0,0,0,0.25), 0 8px 18px rgba(0,0,0,0.18)',
        position: 'relative',
      }}
    >
      {/* screen */}
      <Box
        sx={{
          width: PHONE_WIDTH,
          height: PHONE_HEIGHT,
          borderRadius: 6,
          bgcolor: background,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <StatusBar dark={statusDark} />
        {children}
      </Box>
      {/* volume / power bump */}
      <Box sx={{ position: 'absolute', right: -3, top: 120, width: 3, height: 60, bgcolor: '#0b0c10', borderRadius: 1 }} />
      <Box sx={{ position: 'absolute', left: -3, top: 160, width: 3, height: 40, bgcolor: '#0b0c10', borderRadius: 1 }} />
    </Box>
  </Box>
);

export default PhoneFrame;
